/**
 * Centralized time formatting utilities.
 *
 * Previously duplicated in:
 *   - src/pages/dashboard/utils.ts
 *   - src/pages/timesheet/TimesheetWeek.tsx
 *   - src/pages/timesheet/TimesheetList.tsx
 *   - src/pages/timesheet/TimesheetAdminReview.tsx
 *
 * All timesheet / dashboard code should import from here.
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface TimeFormatOptions {
  /** 'hhmm' (default) or 'decimal' */
  format?: 'hhmm' | 'decimal';
  /** Number of decimal places when using decimal format (default: 2) */
  decimalPlaces?: number;
  /** Whether to include seconds in HH:MM:SS output */
  showSeconds?: boolean;
  /** Number of rounding decimal places for the underlying number (default: none) */
  roundingPlaces?: number;
}

const DEFAULT_OPTIONS: Required<TimeFormatOptions> = {
  format: 'hhmm',
  decimalPlaces: 2,
  showSeconds: false,
  roundingPlaces: 2,
};

// ---------------------------------------------------------------------------
// Core converters
// ---------------------------------------------------------------------------

/**
 * Convert a decimal hour value to a human-readable time string.
 *
 * @example
 *   decimalToHHMM(1.5)             // "01:30"
 *   decimalToHHMM(1.5, { format: 'decimal' })  // "1.50"
 *   decimalToHHMM(1.5, { showSeconds: true })   // "01:30:00"
 */
export function decimalToHHMM(
  hours: number | string | undefined,
  options: TimeFormatOptions = {},
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (hours === undefined || hours === null || hours === '') return '00:00';

  const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
  if (isNaN(numHours)) return '00:00';

  // Apply optional rounding
  const rounded =
    opts.roundingPlaces !== undefined
      ? parseFloat(numHours.toFixed(opts.roundingPlaces))
      : numHours;

  // Decimal format — just return the number
  if (opts.format === 'decimal') {
    return rounded.toFixed(opts.decimalPlaces);
  }

  // HH:MM(:SS) format
  const totalMinutes = Math.round(rounded * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (opts.showSeconds) {
    const s = Math.round((rounded * 3600) % 60);
    return `${h.toString().padStart(2, '0')}:${m
      .toString()
      .padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  return `${h.toString().padStart(2, '0')}:${m
    .toString()
    .padStart(2, '0')}`;
}

/**
 * Convert a time string (HH:MM or HH:MM:SS) to a decimal hours value.
 * Falls back to parseFloat for plain decimal strings.
 *
 * @example
 *   hhmmToDecimal("01:30")    // 1.50
 *   hhmmToDecimal("1.5")      // 1.50
 *   hhmmToDecimal("02:15:30") // 2.26
 */
export function hhmmToDecimal(timeStr: string, decimalPlaces = 2): number {
  if (!timeStr) return 0;

  // If no colon, treat as a raw decimal value
  if (!timeStr.includes(':')) {
    const parsed = parseFloat(timeStr);
    return isNaN(parsed) ? 0 : parseFloat(parsed.toFixed(decimalPlaces));
  }

  const parts = timeStr.split(':');
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const seconds = parts[2] ? parseInt(parts[2], 10) || 0 : 0;

  return parseFloat(
    (hours + minutes / 60 + seconds / 3600).toFixed(decimalPlaces),
  );
}

// ---------------------------------------------------------------------------
// Higher-level helpers
// ---------------------------------------------------------------------------

/**
 * Format hours for display using the provided options.
 */
export function formatTimeForDisplay(
  hours: number | string,
  options: TimeFormatOptions = {},
): string {
  return decimalToHHMM(hours, options);
}

/**
 * Parse a user-entered time string to a decimal hours value.
 * Accepts both "1:30" and "1.5" inputs.
 */
export function parseTimeInput(
  input: string,
  maxHours = 24,
): number {
  if (!input.trim()) return 0;
  if (input.includes(':')) return hhmmToDecimal(input);
  const parsed = parseFloat(input);
  return isNaN(parsed) ? 0 : Math.min(parsed, maxHours);
}

/**
 * Validate that hours fall within allowed bounds.
 */
export function validateHoursInput(
  hours: number,
  min = 0,
  max = 24,
): { isValid: boolean; message?: string } {
  if (hours < min) {
    return { isValid: false, message: `Minimum hours entry is ${min}` };
  }
  if (hours > max) {
    return { isValid: false, message: `Maximum hours entry is ${max}` };
  }
  return { isValid: true };
}
